import { type PeriodApiEnum, PeriodType } from '../models/Period';
import type { components } from '../endpoint/Schema';

type Language = components['schemas']['Language'];

const MILES_TO_KM = 1.60934;

/**
 * Formats mileage value based on language settings.
 * - For EnGb: displays in miles
 * - For EsEs/ItIt: converts to km
 * All values are rounded to 1 decimal place.
 */
export function formatMileage(
  miles: number | undefined | null,
  language: Language = 'EnGb',
): string {
  if (miles == null) return '-';

  if (language === 'EnGb') {
    return `${miles.toFixed(1)} mi`;
  }

  // Convert miles to km for Spanish and Italian
  const km = miles * MILES_TO_KM;
  return `${km.toFixed(1)} km`;
}

export function noop(): void {}

export function capitalizeFirstLetter(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function getPeriodEnum(period: PeriodType): PeriodApiEnum {
  switch (period) {
    case PeriodType.LAST_7_DAY:
      return 'Last7Days';
    case PeriodType.LAST_14_DAY:
      return 'Last14Days';
    case PeriodType.LAST_30_DAY:
      return 'Last30Days';
    case PeriodType.LAST_60_DAY:
      return 'Last60Days';
    case PeriodType.THIS_MONTH:
      return 'ThisMonth';
    case PeriodType.PREVIOUS_MONTH:
      return 'PreviousMonth';
    case PeriodType.CURRENT_WEEK:
      return 'CurrentWeek';
    case PeriodType.lAST_WEEK:
      return 'LastWeek';
    default:
      return 'AllTime';
  }
}

export function formatNumber(
  value: number | string,
  fractionDigits = 2,
): string {
  const nf = new Intl.NumberFormat('en-UK', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return nf.format(num);
}

export function normalizeString(
  value: string | null | undefined,
): string | null {
  if (value && value.trim().length > 0) {
    return value;
  }
  return null;
}
